// src/components/RoddoRadioView.tsx
import React from 'react';
import {
  roddoRadioMoods,
  roddoRadioTracks,
  type RoddoRadioMoodId,
  type RoddoRadioTrack,
} from '../data/roddoRadioTracks';
import { getRoddoTheme } from '../constants/theme';
import { useRoddoRadio } from '../context/RoddoRadioProvider';
import { RoddoFishIcon } from './icons';

interface RoddoRadioViewProps {
  isDarkMode?: boolean;
  onBack?: () => void;
}

type AlbumFilter = 'all' | 'playlist' | RoddoRadioMoodId;

const PLAYLIST_STORAGE_KEY = 'roddo-radio-my-playlist-track-ids-v1';

function formatTime(seconds: number) {
  if (!Number.isFinite(seconds) || seconds <= 0) return '0:00';
  const minutes = Math.floor(seconds / 60);
  const remainder = Math.floor(seconds % 60);
  return `${minutes}:${String(remainder).padStart(2, '0')}`;
}

const moodEmoji: Record<RoddoRadioMoodId, string> = {
  'album-1': '🎵',
  'album-2': '💿',
};

const readStoredPlaylistIds = () => {
  if (typeof window === 'undefined') return [] as string[];
  try {
    const raw = window.localStorage.getItem(PLAYLIST_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    const validIds = new Set(roddoRadioTracks.map((track) => track.id));
    return parsed.filter((id): id is string => typeof id === 'string' && validIds.has(id));
  } catch {
    return [] as string[];
  }
};

const RoddoRadioView: React.FC<RoddoRadioViewProps> = ({
  isDarkMode = false,
  onBack,
}) => {
  const colors = getRoddoTheme(isDarkMode);
  const {
    currentTrack,
    currentTrackId,
    isPlaying,
    isMuted,
    volume,
    duration,
    currentTime,
    playError,
    togglePlay,
    previousTrack,
    nextTrack,
    selectTrack,
    setVolume,
    toggleMute,
    seekTo,
    autoplayEnabled,
    setAutoplayEnabled,
  } = useRoddoRadio();

  const [selectedFilter, setSelectedFilter] = React.useState<AlbumFilter>(
    currentTrack?.moodId || 'all',
  );
  const [playlistIds, setPlaylistIds] = React.useState<string[]>(readStoredPlaylistIds);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(PLAYLIST_STORAGE_KEY, JSON.stringify(playlistIds));
  }, [playlistIds]);

  const playlistIdSet = React.useMemo(() => new Set(playlistIds), [playlistIds]);

  const visibleTracks = React.useMemo(() => {
    if (selectedFilter === 'all') return roddoRadioTracks;
    if (selectedFilter === 'playlist') {
      const byId = new Map(roddoRadioTracks.map((track) => [track.id, track]));
      return playlistIds.map((id) => byId.get(id)).filter(Boolean) as RoddoRadioTrack[];
    }
    return roddoRadioTracks.filter((track) => track.moodId === selectedFilter);
  }, [playlistIds, selectedFilter]);

  const selectAndPlay = React.useCallback(
    (track: RoddoRadioTrack, shouldPlay = false) => {
      selectTrack(track, { play: shouldPlay });
    },
    [selectTrack],
  );

  const handleFilterChange = React.useCallback(
    (filter: AlbumFilter) => {
      setSelectedFilter(filter);

      if (filter !== 'all' && filter !== 'playlist') {
        const firstTrack = roddoRadioTracks.find((track) => track.moodId === filter);
        if (firstTrack && currentTrack?.moodId !== filter) {
          selectTrack(firstTrack, { play: false });
        }
      }
    },
    [currentTrack?.moodId, selectTrack],
  );

  const addToPlaylist = React.useCallback((trackId: string) => {
    setPlaylistIds((ids) => (ids.includes(trackId) ? ids : [...ids, trackId]));
  }, []);

  const removeFromPlaylist = React.useCallback((trackId: string) => {
    setPlaylistIds((ids) => ids.filter((id) => id !== trackId));
  }, []);

  const pageStyle: React.CSSProperties = {
    color: colors.text,
    background: isDarkMode ? '#070B15' : '#F7F2E8',
  };

  const surface = isDarkMode ? '#101827' : '#FFFCF7';
  const surfaceRaised = isDarkMode ? '#0C1423' : '#F2EBE1';
  const border = isDarkMode ? 'rgba(180,195,220,0.12)' : 'rgba(74,58,42,0.14)';
  const borderStrong = isDarkMode ? 'rgba(180,195,220,0.20)' : 'rgba(74,58,42,0.22)';
  const softText = isDarkMode ? '#AEB8C9' : '#6E6255';
  const faintText = isDarkMode ? '#7F8BA0' : '#8A7D6F';
  const lightAccent = '#2F80ED';
  const darkAccent = '#8A70F2';

  const buttonStyle: React.CSSProperties = {
    background: surfaceRaised,
    border: `1px solid ${borderStrong}`,
    color: colors.text,
  };

  const filterItems: Array<{ id: AlbumFilter; label: string; icon: string; count: number }> = [
    { id: 'all', label: 'All Songs', icon: '♪', count: roddoRadioTracks.length },
    { id: 'playlist', label: 'My Playlist', icon: '♥', count: playlistIds.length },
    ...roddoRadioMoods.map((mood) => ({
      id: mood.id as AlbumFilter,
      label: mood.label,
      icon: moodEmoji[mood.id],
      count: roddoRadioTracks.filter((track) => track.moodId === mood.id).length,
    })),
  ];

  return (
    <div className="min-h-full px-3 pb-10 pt-3 sm:px-5" style={pageStyle}>
      <div className="mx-auto max-w-4xl">
        {/* Compact masthead */}
        <header className="grid grid-cols-[44px_1fr_44px] items-center gap-3 border-b pb-4" style={{ borderColor: border }}>
          <div className="grid h-11 w-11 place-items-center rounded-[17px]" style={{ background: surfaceRaised }}>
            <RoddoFishIcon className="h-7 w-8" mode="png" animated={false} />
          </div>

          <div className="min-w-0 text-center">
            <h1 className="text-[27px] font-black leading-none tracking-[-0.04em]">
              Roddo Radio
            </h1>
            <p className="mt-1 text-[12px] font-semibold" style={{ color: softText }}>
              Fishing music for the water.
            </p>
          </div>

          {onBack ? (
            <button
              type="button"
              onClick={onBack}
              aria-label="Back"
              className="grid h-10 w-10 place-items-center justify-self-end rounded-full text-xl font-black transition active:scale-95"
              style={buttonStyle}
            >
              ‹
            </button>
          ) : (
            <div />
          )}
        </header>

        {/* Now playing hero */}
        <section
          className="mt-4 overflow-hidden rounded-[18px] border p-4"
          style={{
            background: surface,
            borderColor: borderStrong,
            boxShadow: isDarkMode
              ? '0 5px 14px rgba(0,0,0,0.15)'
              : '0 4px 14px rgba(89,65,37,0.06)',
          }}
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex min-w-0 items-center gap-3">
              <div
                className="grid h-14 w-14 shrink-0 place-items-center rounded-[14px] border"
                style={{
                  background: isDarkMode ? '#0F1828' : '#F2EBE1',
                  borderColor: borderStrong,
                }}
              >
                <RoddoFishIcon className="h-8 w-9" mode="png" animated={false} />
              </div>

              <div className="min-w-0">
                <div className="text-[10px] font-black uppercase tracking-[0.17em]" style={{ color: faintText }}>
                  Now playing
                </div>
                <h2 className="mt-1 truncate text-[22px] font-black leading-tight tracking-[-0.03em] sm:text-[28px]">
                  {currentTrack?.title || 'Roddo Radio'}
                </h2>
                <div className="mt-1 truncate text-xs font-semibold" style={{ color: softText }}>
                  {currentTrack?.artist || 'Roddo'} · {currentTrack?.moodLabel || 'Original soundtrack'}
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setAutoplayEnabled(!autoplayEnabled)}
              aria-pressed={autoplayEnabled}
              aria-label={autoplayEnabled ? 'Turn autoplay off' : 'Turn autoplay on'}
              title={autoplayEnabled ? 'Autoplay on' : 'Autoplay off'}
              className="relative grid h-9 w-9 shrink-0 place-items-center rounded-full text-[15px] font-black transition active:scale-95"
              style={{
                background: autoplayEnabled ? '#FFC94A' : surfaceRaised,
                border: `1px solid ${autoplayEnabled ? 'rgba(255,255,255,0.34)' : borderStrong}`,
                color: autoplayEnabled ? '#33240F' : softText,
              }}
            >
              ↻
              <span
                className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border"
                aria-hidden="true"
                style={{
                  background: autoplayEnabled ? '#34D399' : '#73717f',
                  borderColor: surface,
                }}
              />
            </button>
          </div>

          <div className="mt-4 flex items-center justify-center gap-3">
            <button
              type="button"
              onClick={previousTrack}
              aria-label="Previous track"
              className="grid h-10 w-10 place-items-center rounded-full text-xl font-black transition active:scale-95"
              style={buttonStyle}
            >
              ‹
            </button>

            <button
              type="button"
              onClick={togglePlay}
              aria-label={isPlaying ? 'Pause Roddo Radio' : 'Play Roddo Radio'}
              className="grid h-12 w-12 place-items-center rounded-full text-sm font-black shadow-[0_5px_0_rgba(185,117,32,0.24)] transition active:translate-y-[2px] active:shadow-[0_2px_0_rgba(185,117,32,0.20)]"
              style={{
                background: '#FFC94A',
                border: '1px solid rgba(255,255,255,0.42)',
                color: '#2F2410',
              }}
            >
              {isPlaying ? '❚❚' : '▶'}
            </button>

            <button
              type="button"
              onClick={nextTrack}
              aria-label="Next track"
              className="grid h-10 w-10 place-items-center rounded-full text-xl font-black transition active:scale-95"
              style={buttonStyle}
            >
              ›
            </button>
          </div>

          <div className="mt-4">
            <input
              type="range"
              min={0}
              max={Math.max(0, duration || 0)}
              step={1}
              value={Math.min(currentTime, duration || currentTime || 0)}
              onChange={(event) => seekTo(Number(event.currentTarget.value))}
              aria-label="Track progress"
              className="h-2 w-full cursor-pointer accent-[#F4A83F]"
            />

            <div className="mt-1 flex justify-between text-[10px] font-bold tabular-nums" style={{ color: faintText }}>
              <span>{formatTime(currentTime)}</span>
              <span>{formatTime(duration)}</span>
            </div>
          </div>

          <div className="mt-3 flex items-center gap-2.5 border-t pt-3" style={{ borderColor: border }}>
            <button
              type="button"
              onClick={toggleMute}
              aria-label={isMuted ? 'Unmute Roddo Radio' : 'Mute Roddo Radio'}
              className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-sm transition active:scale-95"
              style={buttonStyle}
            >
              {isMuted ? '🔇' : volume < 0.45 ? '🔉' : '🔊'}
            </button>

            <input
              type="range"
              min={0}
              max={1}
              step={0.01}
              value={volume}
              onChange={(event) => setVolume(Number(event.currentTarget.value))}
              aria-label="Roddo Radio volume"
              className="min-w-0 flex-1 cursor-pointer accent-[#2F80ED] dark:accent-[#8A70F2]"
            />

            <span className="w-9 text-right text-[10px] font-black tabular-nums" style={{ color: faintText }}>
              {Math.round(volume * 100)}%
            </span>
          </div>

          {playError ? (
            <div
              className="mt-3 rounded-[16px] border px-3 py-2 text-xs font-bold"
              style={{
                borderColor: 'rgba(242,184,75,0.48)',
                background: isDarkMode ? 'rgba(242,184,75,0.10)' : 'rgba(255,213,106,0.24)',
                color: colors.text,
              }}
            >
              {playError}
            </div>
          ) : null}
        </section>

        {/* Filters */}
        <section className="mt-5">
          <div className="mb-3">
            <h2 className="text-[18px] font-black tracking-[-0.02em]">Music</h2>
            <p className="mt-0.5 text-xs font-semibold" style={{ color: softText }}>
              Pick a vibe or build your own playlist.
            </p>
          </div>

          <div className="relative">
            <div
              className="pointer-events-none absolute inset-y-0 right-0 z-10 w-8"
              style={{
                background: isDarkMode
                  ? 'linear-gradient(90deg, rgba(9,13,24,0), rgba(9,13,24,0.96))'
                  : 'linear-gradient(90deg, rgba(247,242,232,0), rgba(247,242,232,0.96))',
              }}
              aria-hidden="true"
            />

            <div className="scrollbar-none flex gap-5 overflow-x-auto border-b pb-0 pr-8" style={{ borderColor: border }}>
              {filterItems.map((item) => {
                const active = selectedFilter === item.id;

                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => handleFilterChange(item.id)}
                    className="relative shrink-0 pb-2.5 text-xs font-black transition active:scale-[0.98]"
                    style={{
                      color: active ? (isDarkMode ? '#C9C2FF' : lightAccent) : softText,
                    }}
                  >
                    <span className="mr-1.5 text-[11px]" aria-hidden="true">{item.icon}</span>
                    {item.label}

                    {active ? (
                      <span
                        className="absolute inset-x-0 bottom-0 h-[2px] rounded-full"
                        style={{
                          background: isDarkMode ? darkAccent : lightAccent,
                          boxShadow: 'none',
                        }}
                        aria-hidden="true"
                      />
                    ) : null}
                  </button>
                );
              })}
            </div>

            <div className="mt-2 text-[11px] font-black" style={{ color: faintText }}>
              {visibleTracks.length} {visibleTracks.length === 1 ? 'track' : 'tracks'}
            </div>
          </div>
        </section>

        {/* Flat track list */}
        <section className="mt-2 overflow-hidden rounded-[16px] border" style={{ background: surface, borderColor: border }}>
          {selectedFilter === 'playlist' && visibleTracks.length === 0 ? (
            <div className="px-5 py-6 text-center">
              <div className="text-sm font-black">Your playlist is empty</div>
              <div className="mt-1 text-xs font-semibold" style={{ color: softText }}>
                Save a few tracks to build your mix.
              </div>
              <button
                type="button"
                onClick={() => handleFilterChange('all')}
                className="mt-3 rounded-full px-4 py-2 text-xs font-black"
                style={{
                  background: isDarkMode ? 'rgba(138,112,242,0.11)' : 'rgba(47,128,237,0.08)',
                  border: `1px solid ${isDarkMode ? 'rgba(138,112,242,0.22)' : 'rgba(47,128,237,0.22)'}`,
                  color: isDarkMode ? '#C9C2FF' : lightAccent,
                }}
              >
                Browse all songs
              </button>
            </div>
          ) : (
            visibleTracks.map((track, index) => {
              const active = track.id === currentTrackId;
              const inPlaylist = playlistIdSet.has(track.id);

              return (
                <div
                  key={track.id}
                  className="group relative flex items-center gap-3 px-3 py-2.5 sm:px-4"
                  style={{
                    background: active
                      ? isDarkMode
                        ? 'rgba(138,112,242,0.09)'
                        : 'rgba(47,128,237,0.06)'
                      : 'transparent',
                    borderTop: index === 0 ? 'none' : `1px solid ${border}`,
                  }}
                >
                  {active ? (
                    <span
                      className="absolute inset-y-2 left-0 w-[3px] rounded-r-full"
                      style={{
                        background: isDarkMode ? darkAccent : lightAccent,
                        boxShadow: 'none',
                      }}
                      aria-hidden="true"
                    />
                  ) : null}

                  <button
                    type="button"
                    onClick={() => selectAndPlay(track, isPlaying)}
                    className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-[10px] font-black transition active:scale-95"
                    style={{
                      background: active ? '#FFC94A' : surfaceRaised,
                      border: `1px solid ${active ? 'rgba(255,255,255,0.30)' : borderStrong}`,
                      color: active ? '#2F2410' : colors.text,
                    }}
                    aria-label={active && isPlaying ? `Pause ${track.title}` : `Play ${track.title}`}
                  >
                    {active && isPlaying ? '❚❚' : '▶'}
                  </button>

                  <button
                    type="button"
                    onClick={() => selectAndPlay(track, false)}
                    className="min-w-0 flex-1 text-left"
                    style={{ color: colors.text }}
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="w-5 shrink-0 text-[9px] font-black tabular-nums" style={{ color: faintText }}>
                        {String(index + 1).padStart(2, '0')}
                      </span>
                      <span className="truncate text-sm font-black">{track.title}</span>
                    </div>
                    <div className="mt-0.5 truncate pl-7 text-[11px] font-semibold" style={{ color: softText }}>
                      {track.artist} · {track.moodLabel}
                    </div>
                  </button>

                  {active ? (
                    <span className="hidden text-[9px] font-black uppercase tracking-[0.12em] sm:block" style={{ color: '#D49A35' }}>
                      {isPlaying ? 'Playing' : 'Ready'}
                    </span>
                  ) : null}

                  <button
                    type="button"
                    onClick={() => (inPlaylist ? removeFromPlaylist(track.id) : addToPlaylist(track.id))}
                    aria-label={inPlaylist ? `Remove ${track.title} from My Playlist` : `Add ${track.title} to My Playlist`}
                    title={inPlaylist ? 'Remove from playlist' : 'Add to playlist'}
                    className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-base font-black transition active:scale-95"
                    style={{
                      background: inPlaylist
                        ? isDarkMode
                          ? 'rgba(138,112,242,0.11)'
                          : 'rgba(47,128,237,0.08)'
                        : 'transparent',
                      border: `1px solid ${inPlaylist ? (isDarkMode ? 'rgba(138,112,242,0.24)' : 'rgba(47,128,237,0.24)') : border}`,
                      color: inPlaylist ? (isDarkMode ? '#C9C2FF' : lightAccent) : faintText,
                    }}
                  >
                    {inPlaylist ? '♥' : '+'}
                  </button>
                </div>
              );
            })
          )}
        </section>
      </div>
    </div>
  );
};

export default RoddoRadioView;
